import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Home from './pages/Home';
import Catalog from './pages/Catalog';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Success from './pages/Success';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductForm from './pages/admin/AdminProductForm';
import AdminCategories from './pages/admin/AdminCategories';
import AdminOrders from './pages/admin/AdminOrders';
import AdminHeroSettings from './pages/admin/AdminHeroSettings';
import AdminMaintenance from './pages/admin/AdminMaintenance';
import AdminDatabase from './pages/admin/AdminDatabase';
import ChangePassword from './pages/admin/ChangePassword';
import ResetPassword from './pages/admin/ResetPassword';
import RequestReset from './pages/admin/RequestReset';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import MaintenancePage from './components/MaintenancePage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to check maintenance mode
const MaintenanceCheck = ({ children }) => {
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  // Allow admin routes to bypass maintenance
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await axios.get(`${API}/settings/maintenance`);
        setMaintenance(response.data);
      } catch (error) {
        console.error('Error checking maintenance:', error);
        setMaintenance({ enabled: false });
      } finally {
        setLoading(false);
      }
    };
    
    checkMaintenance();
  }, [location.pathname]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Show maintenance page for non-admin routes when maintenance is enabled
  if (maintenance?.enabled && !isAdminRoute) {
    return <MaintenancePage message={maintenance.message} />;
  }
  
  return children;
};

function AppContent() {
  return (
    <MaintenanceCheck>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/success" element={<Success />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/products/new" element={<AdminProductForm />} />
            <Route path="/admin/products/edit/:id" element={<AdminProductForm />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/hero-settings" element={<AdminHeroSettings />} />
            <Route path="/admin/maintenance" element={<AdminMaintenance />} />
            <Route path="/admin/database" element={<AdminDatabase />} />
            <Route path="/admin/change-password" element={<ChangePassword />} />
            <Route path="/admin/request-reset" element={<RequestReset />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
          </Routes>
        </main>
        <Footer />
        <Toaster />
      </div>
    </MaintenanceCheck>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;